import React from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import { BrowserTracing } from '@sentry/tracing'
import {
  isProduction,
  isTest,
} from '@common-sense-privacy/common'

import {
  isStaging, isDemo,
} from '@common-sense-privacy/common/src'

import BrowserRouter from '@/components/BrowserRouter'

import config, { version } from '@/config'

import NewRelicContextProvider from '@/context/NewRelicContext'

import theme from '@/theme'

import log from '@/utils/log'
import Sentry from '@/utils/sentry'

import OnNavigationChange from '@/components/OnNavigationChange'
import MenuContextProvider from '@/components/ScreenWrapper/Header/Menu/context'
import UiProvider from '@/components/UiProvider'

import DevMenu from '@/components/DevMenu'

import Alert from '@/components/Alert'
import { useAlert } from '@/context/AlertContext'
import SignupContextProvider from '@/context/SignupContext'

import Router, { history } from './Router'

if (config.sentryEnabled) {
  if (!config.sentryDsn) {
    log('error', '🐞 Missing sentryDsn in config.')
  }
  else {
    Sentry.init({
      dsn: config.sentryDsn,
      debug: !isProduction,
      environment: config.appEnv,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
            React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
        }),
      ],
      release: version,
      tracesSampleRate: 1.0,
    })
  }
}

function App(): React.ReactElement {
  const {
    alert,
    setAlert,
  } = useAlert()

  return (
    <BrowserRouter history={history}>
      <UiProvider theme={theme}>
        <NewRelicContextProvider>
          <MenuContextProvider>
            <SignupContextProvider>
              {/* TODO: Remove circular reference error */}
              {/* can't access lexical declaration '__WEBPACK_DEFAULT_EXPORT__' before initialization */}
              {!isProduction && !isDemo && !isTest && !isStaging && <DevMenu />}
              <OnNavigationChange />

              <Router />
              {alert ? (
                <Alert
                  openError={true}
                  onClose={() => {
                    setAlert(undefined)
                  }}
                  type={alert.type}
                  description={alert.description}
                />
              ) : undefined}
            </SignupContextProvider>
          </MenuContextProvider>
        </NewRelicContextProvider>
      </UiProvider>
    </BrowserRouter>
  )
}

export default App
